<template>
  <div
    v-if="!isOnAgentPage && !isViewer"
    class="fixed z-40 flex flex-col items-end"
    style="bottom: calc(1rem + var(--safe-bottom)); right: calc(1rem + var(--safe-right))">
    <!-- Mini chat window -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-2">
      <div
        v-show="showMiniChat"
        class="mb-4 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <!-- Mini chat header -->
        <div class="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-purple-600">
          <div class="flex items-center gap-2">
            <img :src="brand.images.logoHeader" :alt="brand.agentName" class="w-7 h-7 rounded-full object-cover" />
            <span class="text-sm font-semibold text-white">{{ brand.agentName }}</span>
          </div>
          <div class="flex items-center gap-2">
            <button @click="openFullView" class="p-1 hover:bg-white/20 rounded transition-colors">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
            <button @click="showMiniChat = false" class="p-1 hover:bg-white/20 rounded transition-colors">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Top voice controls -->
        <div
          class="p-3 border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm space-y-2">
          <div class="flex items-center gap-2">
            <select
              v-model="selectedDeviceId"
              @change="onDeviceChange"
              class="flex-1 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
              <option value="">{{ tt('Mic: system default', 'Mikrofon: sistemski privzeti') }}</option>
              <option v-for="d in audioDevices" :key="d.deviceId" :value="d.deviceId">
                {{ d.label || tt(`Mic ${d.deviceId.slice(0, 6)}`, `Mikrofon ${d.deviceId.slice(0, 6)}`) }}
              </option>
            </select>
            <button
              @click="refreshDevices"
              class="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              :title="tt('Refresh microphones', 'Osveži mikrofone')">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          <div class="flex items-center justify-between gap-2">
            <button
              @click="toggleWakeWord"
              class="px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
              :class="
                agentStore.wakeWordEnabled
                  ? 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/45'
                  : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              "
              :title="
                agentStore.wakeWordEnabled
                  ? tt('Wake word on - click to disable', 'Aktivacijska beseda vklopljena – klik za izklop')
                  : tt('Wake word off - click to enable', 'Aktivacijska beseda izklopljena – klik za vklop')
              ">
              {{
                agentStore.wakeWordEnabled
                  ? tt('Wake word on', 'Aktivacijska beseda vklopljena')
                  : tt('Wake word off', 'Aktivacijska beseda izklopljena')
              }}
            </button>
            <span class="text-[11px] font-medium" :class="wakeWordStatusClass">
              {{ wakeWordStatusLabel }}
            </span>
          </div>

          <div
            v-if="agentStore.bluetoothMicLikelyLowQuality"
            class="flex items-start justify-between gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5">
            <p class="text-[11px] text-amber-800 dark:text-amber-200 leading-snug">
              {{
                tt(
                  'Bluetooth mic selected. Voice quality can drop in call mode.',
                  'Izbran je Bluetooth mikrofon. Kakovost zvoka se lahko v načinu klica poslabša.',
                )
              }}
            </p>
            <button
              @click="preferBuiltInMic"
              class="shrink-0 text-[11px] font-medium text-amber-900 dark:text-amber-100 underline hover:no-underline">
              {{ tt('Use built-in', 'Uporabi vgrajeni mikrofon') }}
            </button>
          </div>
        </div>

        <!-- Otto 3D character in chat -->
        <div
          class="flex flex-col items-center pt-2 pb-0 bg-gradient-to-b from-blue-50 to-transparent dark:from-gray-800 dark:to-transparent">
          <OttoDog
            ref="ottoDogRef"
            mode="full"
            :animation="chatAnimation"
            :width="352"
            :height="280"
            :keyboard-control="true" />
          <!-- Poses drawer toggle -->
          <button
            v-if="ottoDogRef?.availableClips?.length"
            @click="showActions = !showActions"
            class="w-full flex items-center justify-center gap-1 py-1 text-[10px] text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 transition-colors border-t border-gray-100 dark:border-gray-700">
            <span>{{ showActions ? tt('Hide poses', 'Skrij poze') : tt('Show poses', 'Pokaži poze') }}</span>
            <svg
              class="w-3 h-3 transition-transform"
              :class="{ 'rotate-180': showActions }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <!-- Pose action buttons drawer -->
          <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-24"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 max-h-24"
            leave-to-class="opacity-0 max-h-0">
            <div
              v-if="showActions"
              class="overflow-y-auto w-full flex flex-wrap justify-center gap-1 px-3 pb-2"
              style="max-height: 6rem">
              <button
                v-for="clip in ottoDogRef!.availableClips"
                :key="clip"
                @click="
                  activeClipName = clip;
                  ottoDogRef!.playClipByName(clip);
                "
                class="px-2 py-0.5 text-[10px] rounded-full border transition-colors"
                :class="
                  clip === activeClipName
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:text-blue-500'
                ">
                {{ clip }}
              </button>
            </div>
          </transition>
        </div>

        <!-- Mini chat messages -->
        <div class="h-64 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
          <div v-if="!agentStore.hasMessages" class="text-center py-8">
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ tt('How can I help you?', 'Kako vam lahko pomagam?') }}
            </p>
          </div>
          <div
            v-for="message in recentMessages"
            :key="message.id"
            class="flex"
            :class="message.role === 'user' ? 'justify-end' : 'justify-start'">
            <div
              class="max-w-[80%] rounded-lg px-3 py-2 text-sm"
              :class="
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
              ">
              {{ message.content }}
              <span v-if="message.source === 'gemini'" class="inline-block ml-1 text-[10px] opacity-50" title="Gemini"
                >✦</span
              >
            </div>
          </div>
          <div v-if="agentStore.isProcessing" class="flex justify-start">
            <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
              <div class="flex gap-1">
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mini chat input -->
        <div class="p-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div v-if="agentStore.isListening" class="mb-2">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[11px] text-red-600 dark:text-red-400 font-medium">{{
                tt('Listening...', 'Poslušam...')
              }}</span>
              <span class="text-[11px] text-gray-500 dark:text-gray-400">{{ agentStore.audioLevel }}%</span>
            </div>
            <div class="w-full h-1.5 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                class="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 transition-all duration-100"
                :style="{ width: `${Math.min(100, Math.max(2, agentStore.audioLevel))}%` }"></div>
            </div>
          </div>

          <div v-if="agentStore.isProcessing && !agentStore.isListening" class="mb-2">
            <button
              @click="cancelProcessing"
              class="w-full px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">
              {{ tt('Cancel processing', 'Prekliči obdelavo') }}
            </button>
          </div>

          <div class="flex items-center gap-2">
            <button
              @click="toggleVoice"
              :disabled="agentStore.isProcessing && !agentStore.isListening"
              class="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all"
              :class="
                agentStore.isListening
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
              ">
              <svg v-if="!agentStore.isListening" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              <svg v-else class="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            </button>
            <input
              v-model="miniInput"
              @keydown.enter.prevent="sendMiniMessage"
              @keydown.up.prevent="onHistoryUp"
              @keydown.down.prevent="onHistoryDown"
              type="text"
              :placeholder="tt('Type a message...', 'Vnesite sporočilo...')"
              class="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              :disabled="agentStore.isProcessing" />
            <button
              @click="sendMiniMessage"
              :disabled="!miniInput.trim() || agentStore.isProcessing"
              class="flex-shrink-0 w-9 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- FAB Button -->
    <button
      @click="toggleMiniChat"
      class="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group overflow-hidden"
      :class="{ 'ring-4 ring-blue-300 dark:ring-blue-800': showMiniChat }">
      <transition
        mode="out-in"
        enter-active-class="transition-transform duration-150"
        enter-from-class="scale-0 rotate-90"
        enter-to-class="scale-100 rotate-0"
        leave-active-class="transition-transform duration-150"
        leave-from-class="scale-100 rotate-0"
        leave-to-class="scale-0 -rotate-90">
        <img
          v-if="!showMiniChat"
          key="otto"
          :src="brand.images.logoHeader"
          :alt="brand.agentName"
          class="w-11 h-11 rounded-full object-cover" />
        <svg v-else key="close" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </transition>

      <!-- Notification badge -->
      <span
        v-if="hasNewMessage && !showMiniChat"
        class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold animate-pulse">
        !
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { brand } from '../config/brand';
import { useAgentStore } from '../stores/agent';
import { useDemoGuest } from '../composables/useDemoGuest';
import OttoDog from './OttoDog.vue';
import type { OttoAnimation } from '../composables/useOttoDog';
import { preloadOttoSounds } from '../utils/ottoSounds';
import { tt } from '../utils/consoleText';

const agentStore = useAgentStore();
/**
 * The agent float is an operator's surface, not a guest's (card `t_803e86a4`:
 * "Agent" is on the viewer's hidden list) — a viewer never sees the button, so
 * neither the drawer's toggle for it nor the panel itself is offered.
 */
const { isViewer } = useDemoGuest();
const router = useRouter();
const route = useRoute();
const showMiniChat = ref(false);
const promptHistory = computed(() => agentStore.promptHistory);
const audioDevices = computed(() => agentStore.audioInputDevices);
const selectedDeviceId = ref(agentStore.selectedAudioDeviceId);
let historyIndex = -1;
let savedInput = '';

const isOnAgentPage = computed(() => {
  const name = route.name as string | undefined;
  const path = route.path;
  return name === 'agent' || /\/agent$/.test(path);
});
const miniInput = ref('');
const hasNewMessage = ref(false);
const ottoDogRef = ref<InstanceType<typeof OttoDog> | null>(null);
const activeClipName = ref('');
const showActions = ref(false);
let soundsPreloaded = false;

/** Animation for the full-body dog in chat header */
const chatAnimation = computed<OttoAnimation>(() => {
  if (agentStore.isSpeaking) return 'talk';
  if (agentStore.isProcessing) return 'tiltHead';
  if (agentStore.isListening) return 'perkEars';
  return 'idle';
});

const DOG_CLIP_MAP: Record<string, string> = {
  sit: 'Sitting',
  stay: 'IdleA',
  down: 'Falling',
  play: 'Jump_Inplace',
  come: 'Walk_Forward_Inplace',
  shake: 'Sit_Up',
  spin: 'Attack_Combat',
  bark: 'Barking',
  dance: 'Jump_Inplace',
  stand: 'IdleA',
  fetch: 'Check',
  roll: 'Falling',
};

watch(
  () => agentStore.dogCommand,
  (cmd) => {
    if (!cmd) return;
    const clip = DOG_CLIP_MAP[cmd];
    if (clip) {
      showMiniChat.value = true;
      nextTick(() => {
        ottoDogRef.value?.resume();
        ottoDogRef.value?.playClipByName(clip);
      });
    }
  },
);

const recentMessages = computed(() => {
  return agentStore.messages.slice(-10);
});

/**
 * The wake-word state word beside the button, in the reader's language. The
 * button already names the feature, so the state word stays short.
 */
const wakeWordStatusLabel = computed(() => {
  if (!agentStore.wakeWordEnabled) return tt('wake word off', 'izklopljena');
  if (agentStore.wakeWordProcessing) return tt('processing', 'obdelujem');
  if (agentStore.wakeWordListening) return tt('listening', 'poslušam');
  if (agentStore.wakeWordDetected) return tt('detected', 'zaznano');
  if (agentStore.wakeWordActive) return tt('armed', 'pripravljeno');
  return tt('starting', 'zaganjam');
});

const wakeWordStatusClass = computed(() => {
  if (!agentStore.wakeWordEnabled) return 'text-gray-500 dark:text-gray-400';
  if (agentStore.wakeWordProcessing) return 'text-yellow-700 dark:text-yellow-300';
  if (agentStore.wakeWordListening || agentStore.wakeWordDetected) return 'text-purple-700 dark:text-purple-300';
  return 'text-green-700 dark:text-green-300';
});

function toggleMiniChat() {
  if (!soundsPreloaded) {
    preloadOttoSounds();
    soundsPreloaded = true;
  }
  showMiniChat.value = !showMiniChat.value;
  if (showMiniChat.value) {
    hasNewMessage.value = false;
  }
}

function sendMiniMessage() {
  if (!miniInput.value.trim() || agentStore.isProcessing) return;
  const text = miniInput.value.trim();
  agentStore.pushToHistory(text);
  historyIndex = -1;
  agentStore.sendMessage(text);
  miniInput.value = '';
}

function onHistoryUp() {
  if (!promptHistory.value.length) return;
  if (historyIndex === -1) savedInput = miniInput.value;
  if (historyIndex < promptHistory.value.length - 1) {
    historyIndex++;
    miniInput.value = promptHistory.value[promptHistory.value.length - 1 - historyIndex];
  }
}

function onHistoryDown() {
  if (historyIndex === -1) return;
  historyIndex--;
  miniInput.value =
    historyIndex === -1 ? savedInput : promptHistory.value[promptHistory.value.length - 1 - historyIndex];
}

function toggleVoice() {
  if (agentStore.isListening) {
    agentStore.stopListening();
  } else {
    agentStore.recordingError = null;
    agentStore.startListening();
  }
}

function onDeviceChange() {
  agentStore.setAudioDevice(selectedDeviceId.value);
}

function toggleWakeWord() {
  if (agentStore.wakeWordEnabled) {
    agentStore.disableWakeWord();
  } else {
    agentStore.enableWakeWord();
  }
}

async function preferBuiltInMic() {
  await agentStore.preferBuiltInMicForQuality();
  selectedDeviceId.value = agentStore.selectedAudioDeviceId;
}

async function refreshDevices() {
  await agentStore.loadAudioDevices(true);
  selectedDeviceId.value = agentStore.selectedAudioDeviceId;
}

function cancelProcessing() {
  agentStore.cancelProcessing();
}

function openFullView() {
  showMiniChat.value = false;
  const m = window.location.hash
    ? window.location.hash.match(/#\/h\/([^/]+)/)
    : window.location.pathname.match(/\/h\/([^/]+)/);
  router.push(m ? `/h/${m[1]}/agent` : '/agent');
}

// Initialize agent on component mount
agentStore.initialize();
onMounted(() => {
  agentStore.loadAudioDevices().then(() => {
    selectedDeviceId.value = agentStore.selectedAudioDeviceId;
  });
});
</script>
